/**
 * Horizontal day-tab strip for the itinerary editor + view. One tab per day,
 * single active day rendered below (the parent owns the panel). Keyboard:
 * Left/Right arrows cycle through tabs, Home/End jump to ends. Roving
 * tabindex per WAI-ARIA tabs pattern.
 *
 * Overflow is horizontal scroll (#245 decision 3) — no wrap, no "More…"
 * collapse — so a long Tuscany-style trip scales on every viewport without
 * shifting the panel below.
 *
 * Two drag gestures live here, distinguished by dataTransfer type:
 *   `text/itinerary-stop` — a stop dragged up from a day panel. Hovering a tab
 *     highlights it; holding ~500ms auto-activates it so the user can drop into
 *     a specific position; dropping ON the tab appends to that day.
 *   `text/itinerary-day` — a tab dragged along the strip to reorder the days
 *     themselves (backs onto the `reorderDay` procedure). Ctrl/Cmd + Arrow on
 *     a focused tab is the keyboard equivalent.
 *
 * Tab and panel id conventions used by callers:
 *   tabId   = `day-tab-${day._id}`
 *   panelId = `day-panel-${day._id}`
 */

import { useEffect, useRef, useState } from 'react';

export interface DayTabItem {
  _id: string;
  title?: string;
}

interface ItineraryDayTabsProps {
  days: DayTabItem[];
  activeDayId: string | null;
  onSelect: (dayId: string) => void;
  /** Optional trailing element (e.g., "+ Add day"). Not part of the tablist. */
  trailing?: React.ReactNode;
  /**
   * Drop a stop onto a day tab — appends it to the end of that day. Omit to
   * disable the gesture (read-only view).
   */
  onDropStopOnDay?: (stopId: string, fromDayId: string, toDayId: string) => void;
  /** Reorder the days themselves by dragging a tab. Omit to disable. */
  onReorderDay?: (dayId: string, toOrdinal: number) => void;
}

export const dayTabId = (dayId: string) => `day-tab-${dayId}`;
export const dayPanelId = (dayId: string) => `day-panel-${dayId}`;

const STOP_MIME = 'text/itinerary-stop';
const FROM_DAY_MIME = 'text/itinerary-from-day';
const DAY_MIME = 'text/itinerary-day';

/** Hold a dragged stop over a tab this long before the tab auto-activates. */
const HOVER_ACTIVATE_MS = 500;

const prefersReducedMotion = () => {
  // Typed as always-present, but jsdom (and any non-browser runtime this
  // component gets rendered in) ships without it.
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const ItineraryDayTabs = ({
  days,
  activeDayId,
  onSelect,
  trailing,
  onDropStopOnDay,
  onReorderDay,
}: ItineraryDayTabsProps) => {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Tab currently under a dragged stop or dragged tab (highlight target).
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [draggingDayId, setDraggingDayId] = useState<string | null>(null);
  const hoverTimer = useRef<number | null>(null);

  const clearHoverTimer = () => {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  useEffect(() => clearHoverTimer, []);

  // Keep the active tab in view as the strip scrolls horizontally. `nearest`
  // on both axes so an already-visible tab never yanks the page around.
  useEffect(() => {
    if (!activeDayId) return;
    refs.current[activeDayId]?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, [activeDayId]);

  const focusAndSelect = (id: string) => {
    onSelect(id);
    // Defer focus so the freshly-selected tab can take the roving tabindex.
    requestAnimationFrame(() => refs.current[id]?.focus());
  };

  const onKeyDown = (e: React.KeyboardEvent, currentIdx: number) => {
    if (days.length === 0) return;

    // Ctrl/Cmd + Arrow reorders the DAY itself — the keyboard equivalent of
    // dragging a tab (WCAG 2.1.1: drag-only functionality has to have a
    // keyboard path). The modifier keeps it clear of the plain-Arrow tablist
    // convention, which only moves the selection.
    if ((e.ctrlKey || e.metaKey) && onReorderDay) {
      const delta =
        e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (delta !== 0) {
        e.preventDefault();
        const target = currentIdx + delta;
        if (target < 0 || target > days.length - 1) return;
        const dayId = days[currentIdx]._id;
        onReorderDay(dayId, target);
        // The reordered tab keeps focus as it slides to its new index.
        requestAnimationFrame(() => refs.current[dayId]?.focus());
        return;
      }
    }

    let nextIdx: number | null = null;
    if (e.key === 'ArrowRight') nextIdx = (currentIdx + 1) % days.length;
    if (e.key === 'ArrowLeft')
      nextIdx = (currentIdx - 1 + days.length) % days.length;
    if (e.key === 'Home') nextIdx = 0;
    if (e.key === 'End') nextIdx = days.length - 1;
    if (nextIdx !== null) {
      e.preventDefault();
      focusAndSelect(days[nextIdx]._id);
    }
  };

  // ----- drag-drop ---------------------------------------------------------

  // Only `types` is readable during dragover (getData is restricted until
  // drop), so gesture detection keys off the MIME list.
  const dragKind = (e: React.DragEvent): 'stop' | 'day' | null => {
    if (e.dataTransfer.types.includes(STOP_MIME)) return 'stop';
    if (e.dataTransfer.types.includes(DAY_MIME)) return 'day';
    return null;
  };

  const handleTabDragOver = (e: React.DragEvent, dayId: string) => {
    const kind = dragKind(e);
    if (!kind) return;
    if (kind === 'stop' && !onDropStopOnDay) return;
    if (kind === 'day' && !onReorderDay) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetId !== dayId) setDropTargetId(dayId);
  };

  const handleTabDragEnter = (e: React.DragEvent, dayId: string) => {
    const kind = dragKind(e);
    if (kind !== 'stop' || !onDropStopOnDay) return;
    // Hold-to-activate: reveal the destination day so the user can drop the
    // stop into an exact position instead of only appending.
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => {
      if (dayId !== activeDayId) onSelect(dayId);
    }, HOVER_ACTIVATE_MS);
  };

  const handleTabDragLeave = (dayId: string) => {
    clearHoverTimer();
    setDropTargetId((cur) => (cur === dayId ? null : cur));
  };

  const handleTabDrop = (e: React.DragEvent, dayId: string, idx: number) => {
    e.preventDefault();
    clearHoverTimer();
    setDropTargetId(null);
    const stopId = e.dataTransfer.getData(STOP_MIME);
    if (stopId && onDropStopOnDay) {
      const fromDayId = e.dataTransfer.getData(FROM_DAY_MIME);
      onDropStopOnDay(stopId, fromDayId, dayId);
      return;
    }
    const draggedDayId = e.dataTransfer.getData(DAY_MIME);
    if (draggedDayId && onReorderDay && draggedDayId !== dayId) {
      // Splice semantics: removing the dragged day first shifts every later
      // index down by one, so a rightward move targets `idx - 1`.
      const fromIdx = days.findIndex((d) => d._id === draggedDayId);
      const toOrdinal = fromIdx > -1 && fromIdx < idx ? idx - 1 : idx;
      onReorderDay(draggedDayId, toOrdinal);
    }
  };

  const handleTabDragStart = (e: React.DragEvent, dayId: string) => {
    if (!onReorderDay) return;
    e.dataTransfer.setData(DAY_MIME, dayId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingDayId(dayId);
  };

  const handleTabDragEnd = () => {
    clearHoverTimer();
    setDraggingDayId(null);
    setDropTargetId(null);
  };

  return (
    <div className="flex items-end gap-2 border-b border-[#DBD4C9]">
      <div
        role="tablist"
        aria-label="Days"
        // No wrap — the strip scrolls horizontally past the strip width (#245).
        className="flex min-w-0 flex-1 gap-1 overflow-x-auto overflow-y-hidden scroll-smooth motion-reduce:scroll-auto"
      >
        {days.map((day, idx) => {
          const isActive = day._id === activeDayId;
          const isDropTarget = day._id === dropTargetId;
          const label = day.title || `Day ${idx + 1}`;
          return (
            <button
              key={day._id}
              ref={(el) => {
                refs.current[day._id] = el;
              }}
              type="button"
              role="tab"
              id={dayTabId(day._id)}
              aria-selected={isActive}
              aria-controls={dayPanelId(day._id)}
              tabIndex={isActive ? 0 : -1}
              draggable={Boolean(onReorderDay)}
              onDragStart={(e) => handleTabDragStart(e, day._id)}
              onDragEnd={handleTabDragEnd}
              onDragOver={(e) => handleTabDragOver(e, day._id)}
              onDragEnter={(e) => handleTabDragEnter(e, day._id)}
              onDragLeave={() => handleTabDragLeave(day._id)}
              onDrop={(e) => handleTabDrop(e, day._id, idx)}
              onClick={() => onSelect(day._id)}
              onKeyDown={(e) => onKeyDown(e, idx)}
              className={`relative -mb-px flex h-10 shrink-0 items-center whitespace-nowrap rounded-t-md px-3 text-sm font-medium transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-info-500 ${
                day._id === draggingDayId ? 'opacity-50' : ''
              } ${
                isDropTarget
                  ? 'border-b-2 border-primary-700 bg-primary-100 text-primary-800 ring-2 ring-inset ring-primary-500'
                  : isActive
                    ? 'border-b-2 border-primary-700 bg-[#F2F0EA] text-primary-700'
                    : 'border-b-2 border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {trailing && <div className="shrink-0 pb-1">{trailing}</div>}
    </div>
  );
};
